import { PROTOCOL_VERSION } from '../../server/protocol.js';

export interface GameSeat {
  room: string;
  playerId: string;
}

export interface QuickMatchSeat {
  playerId: string;
}

export interface SoloMatchSeat {
  playerId: string;
}

export interface BrowserLocationLike {
  protocol: string;
  host: string;
  search: string;
}

export interface SessionStorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface CryptoLike {
  getRandomValues<T extends ArrayBufferView>(array: T): T;
}

export type SessionCredential =
  | { kind: 'bootstrap'; token: string; connectAttemptId: string }
  | { kind: 'reconnect'; token: string; connectAttemptId: string };

interface StoredSessionCredential {
  version: 1;
  token: string;
  acknowledged: boolean;
  connectAttemptId?: string;
}

const TOKEN_BYTES = 32;
const BASE64URL = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
const TOKEN_PATTERN = /^[A-Za-z0-9_-]{43}$/u;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/u;

export class SessionCredentialStorageError extends Error {
  constructor(detail?: string) {
    super(`Браузер не может сохранить ключ сессии${detail === undefined ? '.' : `: ${detail}`}`);
    this.name = 'SessionCredentialStorageError';
  }
}

export function gameSeatFromSearch(search: string): GameSeat | undefined {
  const params = new URLSearchParams(search);
  const room = params.get('room');
  const playerId = params.get('playerId');
  return room !== null && room.length > 0 && playerId !== null && playerId.length > 0
    ? { room, playerId }
    : undefined;
}

export function isQuickMatchSearch(search: string): boolean {
  return new URLSearchParams(search).get('quick') === '1';
}

export function isSoloMatchSearch(search: string): boolean {
  return new URLSearchParams(search).get('solo') === '1';
}

/** Same-origin by default; `ws=` remains an explicit manual-development override. */
export function clientUrl(
  location: BrowserLocationLike,
  seat: GameSeat,
  credential: SessionCredential,
): string {
  const params = new URLSearchParams(location.search);
  const requestedOverride = params.get('ws');
  const override = requestedOverride === null
    ? undefined
    : trustedDevelopmentOverride(location, requestedOverride);
  const scheme = location.protocol === 'https:' ? 'wss:' : 'ws:';
  const base = override ?? `${scheme}//${location.host}/ws`;
  const separator = base.includes('?') ? '&' : '?';
  const attempt = `connectAttemptId=${encodeURIComponent(credential.connectAttemptId)}`;
  return credential.kind === 'reconnect'
    ? `${base}${separator}reconnectToken=${encodeURIComponent(credential.token)}&${attempt}&protocolVersion=${PROTOCOL_VERSION}`
    : `${base}${separator}room=${encodeURIComponent(seat.room)}&playerId=${encodeURIComponent(seat.playerId)}&bootstrapToken=${encodeURIComponent(credential.token)}&${attempt}&protocolVersion=${PROTOCOL_VERSION}`;
}

export function quickClientUrl(
  location: BrowserLocationLike,
  seat: QuickMatchSeat,
  credential: SessionCredential,
): string {
  if (credential.kind === 'reconnect') {
    return reconnectClientUrl(location, credential);
  }
  const base = websocketBase(location);
  const separator = base.includes('?') ? '&' : '?';
  return `${base}${separator}quick=1&playerId=${encodeURIComponent(seat.playerId)}`
    + `&bootstrapToken=${encodeURIComponent(credential.token)}`
    + `&connectAttemptId=${encodeURIComponent(credential.connectAttemptId)}`
    + `&protocolVersion=${PROTOCOL_VERSION}`;
}

export function prepareQuickSessionCredential(
  storage: SessionStorageLike,
  seat: QuickMatchSeat,
  crypto: CryptoLike = globalThis.crypto,
): SessionCredential {
  return prepareSessionCredential(storage, quickCredentialSeat(seat), crypto);
}

export function acknowledgeQuickSessionCredential(
  storage: SessionStorageLike,
  seat: QuickMatchSeat,
  reconnectToken: string,
): void {
  acknowledgeSessionCredential(storage, quickCredentialSeat(seat), reconnectToken);
}

export function clearQuickSessionCredential(storage: SessionStorageLike, seat: QuickMatchSeat): void {
  clearSessionCredential(storage, quickCredentialSeat(seat));
}

export function soloClientUrl(
  location: BrowserLocationLike,
  seat: SoloMatchSeat,
  credential: SessionCredential,
): string {
  if (credential.kind === 'reconnect') return reconnectClientUrl(location, credential);
  const base = websocketBase(location);
  const separator = base.includes('?') ? '&' : '?';
  return `${base}${separator}solo=1&playerId=${encodeURIComponent(seat.playerId)}`
    + `&bootstrapToken=${encodeURIComponent(credential.token)}`
    + `&connectAttemptId=${encodeURIComponent(credential.connectAttemptId)}`
    + `&protocolVersion=${PROTOCOL_VERSION}`;
}

export function prepareSoloSessionCredential(
  storage: SessionStorageLike,
  seat: SoloMatchSeat,
  crypto: CryptoLike = globalThis.crypto,
): SessionCredential {
  return prepareSessionCredential(storage, soloCredentialSeat(seat), crypto);
}

export function acknowledgeSoloSessionCredential(
  storage: SessionStorageLike,
  seat: SoloMatchSeat,
  reconnectToken: string,
): void {
  acknowledgeSessionCredential(storage, soloCredentialSeat(seat), reconnectToken);
}

export function clearSoloSessionCredential(storage: SessionStorageLike, seat: SoloMatchSeat): void {
  clearSessionCredential(storage, soloCredentialSeat(seat));
}

export function promoteSoloSessionCredential(
  storage: SessionStorageLike,
  soloSeat: SoloMatchSeat,
  gameSeat: GameSeat,
): void {
  promoteEphemeralSessionCredential(storage, soloCredentialSeat(soloSeat), gameSeat, 'игра с ботом');
}

/**
 * Copy an acknowledged quick-match bearer to its concrete room seat before
 * replacing the browser URL. The quick key is deliberately retained until
 * the caller has changed both its transport mode and history entry.
 */
export function promoteQuickSessionCredential(
  storage: SessionStorageLike,
  quickSeat: QuickMatchSeat,
  gameSeat: GameSeat,
): void {
  promoteEphemeralSessionCredential(storage, quickCredentialSeat(quickSeat), gameSeat, 'быстрый матч');
}

function promoteEphemeralSessionCredential(
  storage: SessionStorageLike,
  ephemeralSeat: GameSeat,
  gameSeat: GameSeat,
  label: string,
): void {
  const stored = readStored(storage, sessionCredentialStorageKey(ephemeralSeat));
  if (stored === undefined || !stored.acknowledged) {
    throw new SessionCredentialStorageError(`режим «${label}» ещё не подтвердил ключ сессии`);
  }
  writeAndVerify(storage, sessionCredentialStorageKey(gameSeat), {
    version: 1,
    token: stored.token,
    acknowledged: true,
  });
}

function quickCredentialSeat(seat: QuickMatchSeat): GameSeat {
  return { room: '@quick', playerId: seat.playerId };
}

function soloCredentialSeat(seat: SoloMatchSeat): GameSeat {
  return { room: '@solo', playerId: seat.playerId };
}

function reconnectClientUrl(location: BrowserLocationLike, credential: SessionCredential): string {
  const base = websocketBase(location);
  const separator = base.includes('?') ? '&' : '?';
  return `${base}${separator}reconnectToken=${encodeURIComponent(credential.token)}`
    + `&connectAttemptId=${encodeURIComponent(credential.connectAttemptId)}`
    + `&protocolVersion=${PROTOCOL_VERSION}`;
}

function websocketBase(location: BrowserLocationLike): string {
  const params = new URLSearchParams(location.search);
  const requestedOverride = params.get('ws');
  const override = requestedOverride === null
    ? undefined
    : trustedDevelopmentOverride(location, requestedOverride);
  const scheme = location.protocol === 'https:' ? 'wss:' : 'ws:';
  return override ?? `${scheme}//${location.host}/ws`;
}

function trustedDevelopmentOverride(
  location: BrowserLocationLike,
  value: string,
): string | undefined {
  const page = new URL(`${location.protocol}//${location.host}`);
  if (!isLoopbackHost(page.hostname)) return undefined;
  let target: URL;
  try {
    target = new URL(value);
  } catch {
    return undefined;
  }
  return (target.protocol === 'ws:' || target.protocol === 'wss:') && isLoopbackHost(target.hostname)
    ? target.href
    : undefined;
}

function isLoopbackHost(hostname: string): boolean {
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]';
}

export function sessionCredentialStorageKey(seat: GameSeat): string {
  return `cards:session:v${PROTOCOL_VERSION}:${encodeURIComponent(seat.room)}:${encodeURIComponent(seat.playerId)}`;
}

/**
 * Prepare one crash-safe physical bind. Until a `session` ACK arrives every
 * transport retry reads this exact token/attempt pair back from storage.
 */
export function prepareSessionCredential(
  storage: SessionStorageLike,
  seat: GameSeat,
  crypto: CryptoLike = globalThis.crypto,
): SessionCredential {
  const key = sessionCredentialStorageKey(seat);
  const stored = readStored(storage, key);
  const record: StoredSessionCredential = stored ?? {
    version: 1,
    token: randomToken(crypto),
    acknowledged: false,
  };
  const next: StoredSessionCredential = record.connectAttemptId === undefined
    ? { ...record, connectAttemptId: randomUuid(crypto) }
    : record;
  writeAndVerify(storage, key, next);
  return {
    kind: next.acknowledged ? 'reconnect' : 'bootstrap',
    token: next.token,
    connectAttemptId: next.connectAttemptId!,
  };
}

/** Mark the raw bootstrap token as the reconnect bearer and retire its attempt. */
export function acknowledgeSessionCredential(
  storage: SessionStorageLike,
  seat: GameSeat,
  reconnectToken: string,
): void {
  const key = sessionCredentialStorageKey(seat);
  const stored = readStored(storage, key);
  if (stored === undefined || stored.token !== reconnectToken) {
    throw new SessionCredentialStorageError('сервер подтвердил неизвестный ключ');
  }
  writeAndVerify(storage, key, { version: 1, token: stored.token, acknowledged: true });
}

/** Terminal protocol outcomes are the only callers allowed to discard the bearer. */
export function clearSessionCredential(storage: SessionStorageLike, seat: GameSeat): void {
  try {
    storage.removeItem(sessionCredentialStorageKey(seat));
  } catch {
    // A terminal session is already unusable. There is no safe recovery action
    // if the browser refuses removal; the versioned key will be rejected again.
  }
}

function readStored(storage: SessionStorageLike, key: string): StoredSessionCredential | undefined {
  let raw: string | null;
  try {
    raw = storage.getItem(key);
  } catch {
    throw new SessionCredentialStorageError('хранилище недоступно');
  }
  if (raw === null) return undefined;
  let value: unknown;
  try {
    value = JSON.parse(raw) as unknown;
  } catch {
    throw new SessionCredentialStorageError('сохранённый ключ повреждён');
  }
  if (!isStoredCredential(value)) {
    throw new SessionCredentialStorageError('сохранённый ключ повреждён');
  }
  return value;
}

function writeAndVerify(storage: SessionStorageLike, key: string, value: StoredSessionCredential): void {
  const serialized = JSON.stringify(value);
  try {
    storage.setItem(key, serialized);
    if (storage.getItem(key) !== serialized) {
      throw new Error('read-back mismatch');
    }
  } catch {
    throw new SessionCredentialStorageError('запись в sessionStorage недоступна');
  }
}

function isStoredCredential(value: unknown): value is StoredSessionCredential {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  const record = value as Record<string, unknown>;
  return record.version === 1
    && typeof record.token === 'string'
    && TOKEN_PATTERN.test(record.token)
    && typeof record.acknowledged === 'boolean'
    && (record.connectAttemptId === undefined
      || (typeof record.connectAttemptId === 'string' && UUID_PATTERN.test(record.connectAttemptId)));
}

function randomToken(crypto: CryptoLike): string {
  const bytes = crypto.getRandomValues(new Uint8Array(TOKEN_BYTES));
  return encodeBase64Url(bytes);
}

function randomUuid(crypto: CryptoLike): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function encodeBase64Url(bytes: Uint8Array): string {
  let result = '';
  for (let index = 0; index < bytes.length; index += 3) {
    const a = bytes[index]!;
    const b = bytes[index + 1];
    const c = bytes[index + 2];
    result += BASE64URL[a >>> 2];
    result += BASE64URL[((a & 0x03) << 4) | ((b ?? 0) >>> 4)];
    if (b !== undefined) result += BASE64URL[((b & 0x0f) << 2) | ((c ?? 0) >>> 6)];
    if (c !== undefined) result += BASE64URL[c & 0x3f];
  }
  return result;
}
